import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import APIClient from '../api.service';
import usePolledResource from '../hooks/usePolledResource';
import * as courseTime from '../utils/courseTime';

// Exported for the test utilities, which supply a zone without standing up the
// polling provider. Application code reads it through useDashboard() below.
export const DashboardContext = createContext();

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error('useDashboard must be used within DashboardProvider');
  }
  return context;
};

export const DashboardProvider = ({ children }) => {
  const [rainHoldActive, setRainHoldActive] = useState(false);
  const [isScheduleModified, setIsScheduleModified] = useState(false);
  const [lastDeploymentTime, setLastDeploymentTime] = useState(null);
  const [deploymentAge, setDeploymentAge] = useState(null);
  const [allNotifications, setAllNotifications] = useState([]);
  const [badgeCount, setBadgeCount] = useState(0);
  // How many satellites this tenant owns. Null means "not known", and that is NOT only the
  // brief window before the first poll: usePolledResource holds `data` null until a poll
  // SUCCEEDS, so an account whose poll is durably denied (401/403) sits at null indefinitely.
  // Consumers fail OPEN on null in both cases, deliberately - see utils/capability, which holds
  // that rule and the reasoning behind it for every consumer.
  const [satelliteCount, setSatelliteCount] = useState(null);
  // Browser zone until the poll delivers the course zone: a closer guess than
  // UTC while every user sits in the course's own zone.
  const [timezone, setTimezone] = useState(() => courseTime.resolveZone(null));

  const fetchStatus = useCallback(
    ({ signal }) => new APIClient().getDashboardStatus({ signal }),
    []
  );
  const { data, isStale, isAccessDenied, isLoading, refetch } = usePolledResource(fetchStatus, 10000);

  // Sync fields only on a SUCCESSFUL poll. On error the hook keeps `data` unchanged, so the
  // last-known values persist instead of snapping to a fabricated all-clear.
  useEffect(() => {
    if (!data) return;
    setRainHoldActive(!!data.rain_hold_status);
    setIsScheduleModified(!!data.schedule_modified_flag);
    setLastDeploymentTime(data.last_deployment_time ?? null);
    setDeploymentAge(data.deployment_age ?? null);
    setAllNotifications(data.error_stations_list || []);
    setBadgeCount(data.unviewed_count || 0);
    // `?? null` not `|| 0`: zero is a meaningful answer here (a pump-house-only tenant), so it
    // must be distinguishable from "the field is absent".
    setSatelliteCount(data.satellite_count ?? null);
    // Course zone for every human-facing time. resolveZone validates the
    // property and falls back to the browser zone, so context always holds a
    // zone the helpers can actually use.
    setTimezone(courseTime.resolveZone(data.timezone));
  }, [data]);

  const value = useMemo(
    () => ({
      rainHoldActive,
      setRainHoldActive,
      isScheduleModified,
      setIsScheduleModified,
      lastDeploymentTime,
      deploymentAge,
      allNotifications,
      setAllNotifications,
      badgeCount,
      setBadgeCount,
      satelliteCount,
      isPageLoading: isLoading,
      isStale,
      isAccessDenied,
      refreshDashboard: refetch,
      timezone,
    }),
    [
      rainHoldActive,
      isScheduleModified,
      lastDeploymentTime,
      deploymentAge,
      allNotifications,
      badgeCount,
      satelliteCount,
      isLoading,
      isStale,
      isAccessDenied,
      refetch,
      timezone,
    ]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
};
